// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type { CodingContext } from '@imprivata-cloud/dataprivacy';
import { Endpoints } from './constants';
import type {
  AuthenticatorsFindByUserIdsRequest,
  AuthenticatorsFindByUserIdsResponse,
  AuthenticatorsUnenrollRequest,
  CustomerAgreementInfoGetResponse,
  DirectoriesFailedSyncFindResponse,
  DirectoryFailedSyncFindRequest,
  DirectorySyncProgressGetRequest,
  DirectorySyncProgressGetResponse,
  DocumentPurpose,
  EntraIdConsentUrlGetRequest,
  EntraIdConsentUrlGetResponse,
  EntraIdTenantSetupResponse,
  FindDirectoriesResponse,
  FindListRequest,
  FindUsersRequest,
  FindUsersResponse,
  GetClientUserSessionResponse,
  GroupForSyncMarkRequest,
  GroupForSyncUnmarkRequest,
  GroupsFindResponse,
  IdpConfigGetRequest,
  IdpConfigGetResponse,
  IdpConfigUpdateRequest,
  IdpConfigUpdateResponse,
  IdpGetUserRequest,
  IdpGetUserResponse,
  IdpMetadataGetResponse,
  IdpMetadataSaveRequest,
  ImprIdpUsersFindRequest,
  ImprIdpUsersFindResponse,
  ImprUser,
  ImprUserDeleteRequest,
  ImprUserSaveRequest,
  ImprUserSaveResponse,
  MetadataSaveResponse,
  OrgPreferencesGetResponse,
  OrgPreferencesUpdateRequest,
  PrepareUploadRespone,
  ResidenceLocationsSaveResponse,
  SpDisplayNameUpdateRequest,
  SpDisplayNameUpdateResponse,
  TempCodeAssignRequest,
  TempCodeAssignResponse,
  TemporaryCodeMetadataGetRequest,
  TemporaryCodeMetadataGetResponse,
  TenantInfoResponse,
  TenantLookupResponse,
  UserUnlockRequest,
} from './types';
import { fetcher } from './utils';

const lookupTenant = async (userEmail: string, workflowId?: string): Promise<TenantLookupResponse> => {
  return fetcher(Endpoints.TENANT_LOOKUP_BY_DOMAIN, {
    method: 'POST',
    body: JSON.stringify({ userEmail }),
    workflowId,
  });
};

const getClientUserSession = async (workflowId: string, tenantId?: string): Promise<GetClientUserSessionResponse> => {
  return fetcher(
    Endpoints.GET_CLIENT_USER_SESSION,
    {
      method: 'POST',
      workflowId,
    },
    tenantId,
  );
};

const getLandingPage = async (workflowId: string, tenantId: string) => {
  return fetcher(
    Endpoints.GET_LANDING_PAGE,
    {
      method: 'POST',
      workflowId,
    },
    tenantId,
  );
};

const getIpRanges = async (tenantId: string): Promise<string[]> => {
  try {
    const response = await fetcher(
      Endpoints.IP_RANGES,
      {
        method: 'POST',
      },
      tenantId,
    );

    return response.cidrs || [];
  } catch (error) {
    console.error('Error fetching IP ranges:', error);
    return [];
  }
};

const findDirectories = async (tenantId: string): Promise<FindDirectoriesResponse> => {
  return fetcher(
    Endpoints.DIR_FIND,
    {
      method: 'POST',
    },
    tenantId,
  );
};

const findUsers = async (tenantId: string, request: FindUsersRequest): Promise<FindUsersResponse> => {
  return fetcher(
    Endpoints.USERS_FIND,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    tenantId,
  );
};

async function getIdpConfigSAML(data: IdpConfigGetRequest, tenantId: string): Promise<IdpConfigGetResponse> {
  return fetcher(
    Endpoints.GET_IDP_CONFIG_SAML,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
}

export async function updateIdpConfigSAML(
  data: IdpConfigUpdateRequest,
  tenantId: string,
): Promise<IdpConfigUpdateResponse> {
  return fetcher(
    Endpoints.SAVE_AND_UPDATE_IDP_CONFIG_SAML,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
}

const getEntraIdConsentUrl = async (
  request: EntraIdConsentUrlGetRequest,
  tenantId: string,
): Promise<EntraIdConsentUrlGetResponse> => {
  return fetcher(
    Endpoints.ENTRA_ID_CONSENT,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    tenantId,
  );
};

const startEntraIdInitialSync = async (tenantId: string) => {
  return fetcher(
    Endpoints.DIR_INITIAL_SYNC,
    {
      method: 'POST',
      body: JSON.stringify({}), //TODO - check with Erik why this is needed.
    },
    tenantId,
  );
};

export async function directoriesSync(tenantId: string) {
  return fetcher(
    Endpoints.DIR_SYNC,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    tenantId,
  );
}

export async function dirSyncProgress(
  tenantId: string,
  directoryId: string,
): Promise<DirectorySyncProgressGetResponse> {
  return fetcher(
    Endpoints.DIR_SYNC_PROGRESS,
    {
      method: 'POST',
      body: JSON.stringify({ directoryId } as DirectorySyncProgressGetRequest),
    },
    tenantId,
  );
}

export async function dirFailedSyncFind(
  tenantId: string,
  directoryId: string,
): Promise<DirectoriesFailedSyncFindResponse> {
  return fetcher(
    Endpoints.DIR_FAILED_SYNC_FIND,
    {
      method: 'POST',
      body: JSON.stringify({ directoryId } as DirectoryFailedSyncFindRequest),
    },
    tenantId,
  );
}

const completeEntraIdSetup = async (tenantId: string): Promise<EntraIdTenantSetupResponse> => {
  return fetcher(
    Endpoints.ENTRA_ID_SETUP_COMPLETE,
    {
      method: 'POST',
    },
    tenantId,
  );
};

const portalLogout = async (tenantId: string) => {
  return fetcher(
    Endpoints.LOGOUT,
    {
      method: 'GET',
    },
    tenantId,
  );
};

export function orgPreferencesGet(): Promise<OrgPreferencesGetResponse> {
  return fetcher(Endpoints.PORTAL_ORG_PREFERENCES_GET, { method: 'POST' });
}

export function orgPreferencesUpdate(data: OrgPreferencesUpdateRequest) {
  return fetcher(Endpoints.PORTAL_ORG_PREFERENCES_UPDATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function idpSave(data: IdpMetadataSaveRequest): Promise<MetadataSaveResponse> {
  return fetcher(Endpoints.IDP_SAVE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

const markGroupForSync = async (data: GroupForSyncMarkRequest, tenantId: string): Promise<void> => {
  return fetcher(
    Endpoints.MARK_GROUP_SYNC,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

const unmarkGroupForSync = async (data: GroupForSyncUnmarkRequest, tenantId: string): Promise<void> => {
  return fetcher(
    Endpoints.UNMARK_GROUP_SYNC,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

const findGroups = async (data: FindListRequest, tenantId: string): Promise<GroupsFindResponse> => {
  return fetcher(
    Endpoints.GROUPS_FIND,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

const findAuthenticatorsByUserIds = async (
  data: AuthenticatorsFindByUserIdsRequest,
  tenantId: string,
): Promise<AuthenticatorsFindByUserIdsResponse> => {
  return fetcher(
    Endpoints.FIND_AUTHENTICATORS,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

const unenrollAuthenticators = async (data: AuthenticatorsUnenrollRequest, tenantId: string): Promise<void> => {
  return fetcher(
    Endpoints.UNENROLL_AUTHENTICATORS,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

const getTestTenantInfo = async (tenantId: string): Promise<TenantInfoResponse> => {
  return fetcher(
    Endpoints.TEST_TENANT_INFO,
    {
      method: 'POST',
    },
    tenantId,
  );
};

const updateSpDisplayName = async (
  data: SpDisplayNameUpdateRequest,
  tenantId: string,
): Promise<SpDisplayNameUpdateResponse> => {
  return fetcher(
    Endpoints.UPDATE_SP_DISPLAY_NAME,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
};

export const findImprIdpUsers = async (
  request: ImprIdpUsersFindRequest,
  tenantId: string,
): Promise<ImprIdpUsersFindResponse> => {
  return fetcher(
    Endpoints.PORTAL_IMPR_IDP_USERS_FIND,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    tenantId,
  );
};

export async function imprIdpUsersSave(request: ImprUserSaveRequest): Promise<ImprUserSaveResponse> {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_SAVE, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export const deleteImprIdpUser = async (payload: ImprUserDeleteRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_DELETE, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

async function getPrepareUploadUrl(filename: string, purpose: DocumentPurpose): Promise<PrepareUploadRespone> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_PREPARE, {
    method: 'POST',
    body: JSON.stringify({ filename, purpose }),
  });
}

async function completeLegalDocumentsUpload(readUrl: string, operationId: string): Promise<PrepareUploadRespone> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_COMPLETE, {
    method: 'POST',
    body: JSON.stringify({ readUrl, operationId }),
  });
}

async function saveLegalDocumentLocations(residenceLocations: string[]): Promise<ResidenceLocationsSaveResponse> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_RESIDENCE_LOCATIONS, {
    method: 'POST',
    body: JSON.stringify({ residenceLocations }),
  });
}

async function getCustomerAgreementInfo(): Promise<CustomerAgreementInfoGetResponse> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_INFO_GET, {
    method: 'POST',
  });
}

async function deleteLegalAgreementDocument(documentPurpose: DocumentPurpose): Promise<void> {
  return fetcher(Endpoints.PORTAL_LEGAL_AGREEMENTS_DELETE, {
    method: 'POST',
    body: JSON.stringify({ documentPurpose }),
  });
}

export async function getUserDetails(request: IdpGetUserRequest): Promise<IdpGetUserResponse> {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USER_DETAILS_GET, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

const assignTempCode = async (
  data: TempCodeAssignRequest,
  codingContext: CodingContext,
): Promise<TempCodeAssignResponse> => {
  return fetcher(Endpoints.PORTAL_TEMP_CODE_ASSIGN, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Impr-Coding-Ctx': codingContext.buildImprCodingCtxHeader(),
    },
  });
};

const getTemporaryCodeMetadata = async (
  data: TemporaryCodeMetadataGetRequest,
): Promise<TemporaryCodeMetadataGetResponse> => {
  return fetcher(Endpoints.PORTAL_TEMP_CODE_METADATA_GET, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const unlockImprIdpUser = async (data: UserUnlockRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_UNLOCK, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export {
  lookupTenant,
  getClientUserSession,
  getEntraIdConsentUrl,
  getLandingPage,
  getIpRanges,
  portalLogout,
  completeEntraIdSetup,
  startEntraIdInitialSync,
  findDirectories,
  getIdpConfigSAML,
  findUsers,
  markGroupForSync,
  unmarkGroupForSync,
  findGroups,
  findAuthenticatorsByUserIds,
  unenrollAuthenticators,
  getTestTenantInfo,
  updateSpDisplayName,
  getPrepareUploadUrl,
  completeLegalDocumentsUpload,
  getCustomerAgreementInfo,
  saveLegalDocumentLocations,
  deleteLegalAgreementDocument,
  assignTempCode,
  getTemporaryCodeMetadata,
};
